// src/components/managed-sources/ManagedSourceWizard.tsx

import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Anchor,
  Box,
  Button,
  Group,
  Loader,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { Link } from "@/routing/accountRouting";
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconArrowRight,
  IconShieldLock,
} from "@tabler/icons-react";
import { ConnectorIcon } from "@/components/data-sources/ConnectorIcon";
import { AppModal } from "@/components/modals/AppModal";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useDwhProvisioning } from "@/context/DwhProvisioningContext";
import { useBranding } from "@/context/BrandingContext";

import { listConnectors, setupManagedSource } from "@/api/managedSources";
import { getTransformOutlookByConnector } from "@/api/transformOutlook";
import {
  OUTLOOK_TOAST_TITLE,
  outlookMessage,
} from "./transformOutlookCopy";
import { getPiiVaultStatus, isPiiVaultReady } from "@/api/piiVault";
import type {
  FacebookAdAccount,
  FreshnessMode,
  GA4BqExportDatasetEntry,
  ManagedSource,
  ManagedSourceConnector,
  ManagedSourceSetupPayload,
  ScheduleInterval,
} from "@/types/managedSources";

import DwhSetupModal from "@/components/dwh/DwhSetupModal";
import { ModalLayerProvider } from "@/components/modals/ModalLayer";
import FacebookAuthStep from "./FacebookAuthStep";
import GoogleAnalyticsAuthStep from "./GoogleAnalyticsAuthStep";
import GoogleAdsAuthStep from "./GoogleAdsAuthStep";
import GoogleSearchConsoleAuthStep from "./GoogleSearchConsoleAuthStep";
import MicrosoftAdsAuthStep from "./MicrosoftAdsAuthStep";
import type {
  MicrosoftAccountSelectionMode,
  MicrosoftCoveragePreset,
} from "./MicrosoftAdsAuthStep";
import GA4BqExportAuthStep from "./GA4BqExportAuthStep";
import BrevoAuthStep from "./BrevoAuthStep";
import ActiveCampaignAuthStep from "./ActiveCampaignAuthStep";
import KlaviyoAuthStep from "./KlaviyoAuthStep";
import CredentialAuthStep from "./CredentialAuthStep";
import ShopifyAuthStep from "./ShopifyAuthStep";
import VerityPixelWebsiteStep from "./VerityPixelWebsiteStep";
import type { VerityPixelConnectorConfig } from "./VerityPixelWebsiteStep";
import VerityPixelInstallPanel from "./VerityPixelInstallPanel";
import { pixelSiteKeyOf } from "@/api/pixelSites";
import { useAuth } from "@/context/AuthContext";
import GenericTransformAuthStep from "./GenericTransformAuthStep";
import CategoriesStep from "./CategoriesStep";
import ReviewStep from "./ReviewStep";

type Props = {
  opened: boolean;
  onClose: () => void;
  /** Fires after the source is created. Receives the created managed source so
   *  imperative callers (e.g. the assistant's connect handoff) can report the
   *  new `data_source_id` / name back. Existing callers that ignore the arg
   *  stay valid. */
  onCreated?: (source: ManagedSource) => void;
  /** Stacking context override. The assistant opens this wizard from the
   *  global drawer, which shares Mantine's default modal z-index (200); the
   *  provider passes a higher value so the wizard sits ABOVE the drawer. */
  zIndex?: number;
  initialConnectorType?: string | null;
  transformOnly?: boolean;
};

type WizardStep = "dwh-gate" | "connect" | "categories" | "confirm";

/** Fallback labels when selectedConnector hasn't loaded yet */
const CONNECTOR_TYPE_LABELS: Record<string, string> = {
  FACEBOOK: "Facebook Ads",
  GOOGLE_ANALYTICS: "Google Analytics",
  GOOGLE_ADS: "Google Ads",
  MICROSOFT_ADS: "Microsoft Ads",
  GA4_BQ_EXPORT: "GA4 BQ Export",
  BREVO: "Brevo",
  ACTIVECAMPAIGN: "ActiveCampaign",
  KLAVIYO: "Klaviyo",
  WOOCOMMERCE: "WooCommerce",
  GOOGLE_SEARCH_CONSOLE: "Google Search Console",
  VERITY_PIXEL: "Verity Pixel",
};

// Connectors whose BYO (transform-only) flow uses the generic dataset-picker
// auth step (GenericTransformAuthStep) and sends only
// { source_project_id, source_dataset_id } — no connector-specific fields.
// New BYO connectors join this list instead of getting their own branch.
const GENERIC_BYO_CONNECTORS = ["FACEBOOK", "GOOGLE_ADS", "MICROSOFT_ADS"];

// No `icon` field: the stepper has always rendered a number or a tick, never a
// per-step glyph. The icons that used to sit here were dead before the chrome
// moved to AppModal — carrying them forward would suggest otherwise.
const VISIBLE_STEPS: { key: WizardStep; label: string }[] = [
  { key: "connect", label: "Connect" },
  { key: "categories", label: "Data" },
  { key: "confirm", label: "Launch" },
];

const STEP_ORDER: WizardStep[] = ["dwh-gate", "connect", "categories", "confirm"];

/**
 * The wizard runs above Mantine's default modal elevation (its caller passes
 * `zIndex={300}` to clear the assistant drawer). Publish that so a modal opened
 * from deep inside a step — `BigQueryAuthorizationCreateModal` from the BYO
 * BigQuery and GA4 BQ Export steps — can match the layer instead of falling
 * back to the default 200 and rendering underneath this wizard's own overlay.
 *
 * Wrapping here rather than inside the body covers both render branches
 * (`dwh-gate` returns early) without threading a prop through four step
 * components that have no use for it. See `components/modals/ModalLayer.tsx`.
 *
 * `AppModal` publishes the same layer again for the main branch. That
 * is deliberate redundancy, not a leftover: this wrapper is what covers the
 * `dwh-gate` branch, which returns before the shared chrome is ever reached.
 * A nested provider carrying the identical value is a no-op for consumers.
 */
const SCHEDULE_INTERVALS: readonly ScheduleInterval[] = ["HOURLY", "TWICE_DAILY", "DAILY", "CUSTOM"];

function scheduleIntervalFromRegistry(value: string | undefined): ScheduleInterval {
  const v = (value ?? "").toUpperCase();
  return (SCHEDULE_INTERVALS as readonly string[]).includes(v) ? (v as ScheduleInterval) : "DAILY";
}

export function ManagedSourceWizard(props: Props) {
  return (
    <ModalLayerProvider zIndex={props.zIndex}>
      <WizardBody {...props} />
    </ModalLayerProvider>
  );
}

// Deliberately NOT named with the ManagedSourceWizard prefix. The structural
// guard in __tests__/singleWizardMount.test.ts scans the source for that
// component's JSX opening tag as a plain substring, so any inner component
// whose name starts with it reads to the guard as a second mount site.
/**
 * The install panel on the wizard's success screen. Its own component so the
 * auth read happens only here, once a pixel source exists, rather than in
 * every wizard render.
 */
function PixelInstallAfterLaunch({ siteKey }: { siteKey: string }) {
  const { user } = useAuth();
  const isAccountAdmin =
    user?.activeAccount?.role === "OWNER" ||
    user?.activeAccount?.role === "ADMIN";
  return <VerityPixelInstallPanel key={siteKey} siteKey={siteKey} isAccountAdmin={isAccountAdmin} />;
}

function WizardBody({ opened, onClose, onCreated, zIndex, initialConnectorType, transformOnly }: Props) {
  const { activeWorkspaceId } = useWorkspace();
  const { provisioningStatus } = useDwhProvisioning();
  const { branding } = useBranding();
  const hasDwh = provisioningStatus === "ACTIVE";

  // --- Flow state ---
  const [step, setStep] = useState<WizardStep>(hasDwh ? "connect" : "dwh-gate");
  const [success, setSuccess] = useState(false);
  // The created source is retained (not just handed to onCreated) because the
  // review step keeps rendering after submit and needs the real source to show
  // its launch progress — a boolean can't identify which pipeline to follow.
  const [createdSource, setCreatedSource] = useState<ManagedSource | null>(null);
  const [dwhReady, setDwhReady] = useState(hasDwh);
  const [dwhModalOpen, setDwhModalOpen] = useState(false);
  // PII vault readiness — gates connectors whose pipeline routes identifiers
  // through the per-account PII vault (e.g. Brevo). Fetched on open.
  const [piiVaultReady, setPiiVaultReady] = useState(false);

  // --- Connectors ---
  const [connectors, setConnectors] = useState<ManagedSourceConnector[]>([]);
  const [selectedConnector, setSelectedConnector] =
    useState<ManagedSourceConnector | null>(null);
  // Why this is tracked instead of swallowed: every auth step below is gated on
  // `selectedConnector`, so a failed connector load renders the Connect step
  // COMPLETELY EMPTY — no picker, no auth step, no message, no spinner. That is
  // indistinguishable from "still loading" or "the wizard is broken", when the
  // actual cause is usually that the API is unreachable.
  const [connectorsLoading, setConnectorsLoading] = useState(false);
  const [connectorsError, setConnectorsError] = useState<
    null | "unreachable" | "connector-missing"
  >(null);

  // --- Auth ---
  const [authorizationId, setAuthorizationId] = useState<string | null>(null);
  const [adAccountId, setAdAccountId] = useState("");
  // Selected Meta ad account's display metadata (name, currency) lifted from
  // the auth step's picker — persisted into the source config at submit and
  // used to disambiguate the default source name. Null for manual entries.
  const [fbAccountMeta, setFbAccountMeta] = useState<FacebookAdAccount | null>(null);
  // Connected Shopify store label (shop name / connection name) for the review step.
  const [shopifyStoreLabel, setShopifyStoreLabel] = useState<string | null>(null);
  // Selected connection name for a generic credential-form connector, shown
  // on the review step.
  const [credentialConnectionLabel, setCredentialConnectionLabel] = useState<string | null>(null);
  const [propertyId, setPropertyId] = useState("");
  const [propertyName, setPropertyName] = useState("");
  const [googleAdsCustomerId, setGoogleAdsCustomerId] = useState("");
  // The manager the selected account must be addressed THROUGH. Empty for a
  // directly-linked account, and empty is a real value here — never substitute a
  // default (see resolveLoginCustomerId in GoogleAdsAuthStep).
  const [googleAdsLoginCustomerId, setGoogleAdsLoginCustomerId] = useState("");

  // --- Google Search Console ---
  // The VERBATIM Google spelling of the selected property
  // (`sc-domain:example.com` or `https://example.com/`). Held and submitted
  // exactly as the picker received it: it is the warehouse union discriminator,
  // and Google returns rows only under the string it gave.
  const [gscSiteUrl, setGscSiteUrl] = useState("");

  // --- Verity Pixel ---
  // The website the pixel goes on, as the setup `connector_config` the
  // connect step built (`{website_id}` or `{website_url, confirm_owned}`), and
  // its host for the review step and the default source name. No
  // authorization: the backend authorizes with the account's own warehouse
  // access.
  const [pixelConfig, setPixelConfig] = useState<VerityPixelConnectorConfig | null>(null);
  const [pixelWebsiteLabel, setPixelWebsiteLabel] = useState<string | null>(null);

  // --- Microsoft Ads ---
  // The chosen MANAGER account (Microsoft calls it a "customer"): its id names
  // the per-manager raw dataset, its name feeds the review + default source name.
  const [msCustomerId, setMsCustomerId] = useState("");
  const [msCustomerName, setMsCustomerName] = useState("");
  // Ingest every account under the manager (incl. future ones) vs a fixed set.
  const [msAccountSelectionMode, setMsAccountSelectionMode] =
    useState<MicrosoftAccountSelectionMode>("ALL");
  const [msAccountIds, setMsAccountIds] = useState<string[]>([]);
  // How much of the Microsoft Advertising surface to model; "standard" is the
  // recommended default.
  const [msCoveragePreset, setMsCoveragePreset] =
    useState<MicrosoftCoveragePreset>("standard");
  const [authValid, setAuthValid] = useState(false);

  // --- GA4 BQ Export ---
  const [sourceProjectId, setSourceProjectId] = useState("");
  const [sourceDatasets, setSourceDatasets] = useState<GA4BqExportDatasetEntry[]>([]);

  // --- Generic transform-only ---
  const [transformSourceProjectId, setTransformSourceProjectId] = useState("");
  const [transformSourceDatasetId, setTransformSourceDatasetId] = useState("");
  const [transformAvailableCategories, setTransformAvailableCategories] = useState<string[] | null>(null);

  // --- Categories ---
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  // --- Schedule & Backfill (defaults, configurable in Review step) ---
  const [scheduleInterval, setScheduleInterval] =
    useState<ScheduleInterval>("DAILY");
  // 05:00 local — must match the backend default (`ManagedPipelineConfig
  // .schedule_hour`): the wizard always posts this value, so the backend
  // default is never reached from here. See plan 2026-08-12-composites-daily-anchor.
  const [scheduleHour, setScheduleHour] = useState(5);
  const [scheduleCron, setScheduleCron] = useState("");
  const [backfillDays, setBackfillDays] = useState(395);

  // --- "When does new data arrive in this source?" (plan 2026-08-08, Step 10).
  //     Bring-your-own-data creates only. `null` = the customer never touched
  //     the question, so the connector's own default applies and the field is
  //     left off the payload entirely — never a client-side guess at the rule.
  const [freshnessMode, setFreshnessMode] = useState<FreshnessMode | null>(null);

  // --- Source name ---
  const [sourceName, setSourceName] = useState("");
  // The "Connection name" the customer typed in the connect step. It names the
  // AUTHORIZATION, not the source, but customers read the two as one thing and
  // reported the source name "overwriting" what they had just typed. Empty
  // whenever an EXISTING authorization was picked instead of a new one being
  // created, which is exactly when the connector-label default should win.
  const [connectionName, setConnectionName] = useState("");

  // --- Account-overview opt-in (default TRUE).
  //     See docs/plans/2026-05-17-managed-dashboard-per-datasource.md. ---
  const [includeInOverview, setIncludeInOverview] = useState(true);

  // --- Brevo-only: Verity-ID write-back opt-in (default FALSE — it writes
  //     to the customer's Brevo account). Toggle lives in the Review step's
  //     Advanced settings; same setting as the detail drawer's Brevo options. ---
  const [verityIdWriteback, setVerityIdWriteback] = useState(false);

  // --- Use this source for segmentation (default FALSE — turning on a
  //     segmentation surface is an explicit act). Only offered by connectors
  //     that declare `segmentation_supported`; the same setting is the
  //     detail drawer's Segmentation toggle. ---
  const [useAsSegmentationSource, setUseAsSegmentationSource] = useState(false);

  // --- Submit ---
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // --- Reset on open ---
  useEffect(() => {
    if (!opened) return;
    setStep(hasDwh ? "connect" : "dwh-gate");
    setSuccess(false);
    setCreatedSource(null);
    setDwhReady(hasDwh);
    setDwhModalOpen(false);
    setPiiVaultReady(false);
    setSelectedConnector(null);
    setConnectorsError(null);
    setConnectorsLoading(false);
    setAuthorizationId(null);
    setAdAccountId("");
    setFbAccountMeta(null);
    setShopifyStoreLabel(null);
    setCredentialConnectionLabel(null);
    setPropertyId("");
    setPropertyName("");
    setGoogleAdsCustomerId("");
    setGoogleAdsLoginCustomerId("");
    setGscSiteUrl("");
    setPixelConfig(null);
    setPixelWebsiteLabel(null);
    setMsCustomerId("");
    setMsCustomerName("");
    setMsAccountSelectionMode("ALL");
    setMsAccountIds([]);
    setMsCoveragePreset("standard");
    setAuthValid(false);
    setSourceProjectId("");
    setSourceDatasets([]);
    setTransformSourceProjectId("");
    setTransformSourceDatasetId("");
    setTransformAvailableCategories(null);
    setSelectedCategories([]);
    setSourceName("");
    setConnectionName("");
    setIncludeInOverview(true);
    setVerityIdWriteback(false);
    setUseAsSegmentationSource(false);
    setScheduleInterval("DAILY");
    setScheduleHour(6);
    setScheduleCron("");
    setBackfillDays(395);
    setFreshnessMode(null);
    setSubmitError(null);
    setSubmitting(false);

    // Load connectors (auto-select if only one)
    loadConnectors();
    loadPiiVaultStatus();
  }, [opened]);

  function selectConnector(connector: ManagedSourceConnector) {
    setSelectedConnector(connector);
    // The connector's registry default (the pixel runs HOURLY: its profiles
    // exist for the audiences, plan step 7.3); anything the registry does not
    // name falls back to DAILY, the wizard's own default.
    setScheduleInterval(scheduleIntervalFromRegistry(connector.default_schedule));
    setAuthorizationId(null);
    setAdAccountId("");
    setFbAccountMeta(null);
    setShopifyStoreLabel(null);
    setCredentialConnectionLabel(null);
    setPropertyId("");
    setPropertyName("");
    setGoogleAdsCustomerId("");
    setGoogleAdsLoginCustomerId("");
    setGscSiteUrl("");
    setPixelConfig(null);
    setPixelWebsiteLabel(null);
    setMsCustomerId("");
    setMsCustomerName("");
    setMsAccountSelectionMode("ALL");
    setMsAccountIds([]);
    setMsCoveragePreset("standard");
    setSourceProjectId("");
    setSourceDatasets([]);
    setTransformSourceProjectId("");
    setTransformSourceDatasetId("");
    setTransformAvailableCategories(null);
    setVerityIdWriteback(false);
    setUseAsSegmentationSource(false);
    // Back to "untouched": the pre-selected answer is a property of the
    // CONNECTOR, so a choice made for a previous one must not carry over.
    setFreshnessMode(null);
    setAuthValid(false);
    const mvpCategories = connector.categories
      .filter((c) => c.available)
      .map((c) => c.key);
    setSelectedCategories(
      mvpCategories.length > 0
        ? mvpCategories
        : connector.categories.map((c) => c.key)
    );
    setBackfillDays(connector.default_backfill_days ?? 395);
  }

  async function loadPiiVaultStatus() {
    try {
      const s = await getPiiVaultStatus();
      setPiiVaultReady(isPiiVaultReady(s));
    } catch {
      // Non-fatal. Default to not-ready: only vault-requiring connectors
      // (e.g. Brevo) are gated, so over-gating here is the safe choice —
      // better than letting the connect fail later at runtime.
      setPiiVaultReady(false);
    }
  }

  async function loadConnectors() {
    setConnectorsLoading(true);
    setConnectorsError(null);
    try {
      const list = await listConnectors();
      setConnectors(list);
      // Auto-select if connector type was pre-selected from the Add Data Source modal
      if (initialConnectorType) {
        const match = list.find((c) => c.connector_type === initialConnectorType);
        if (match) {
          selectConnector(match);
          return;
        }
        // Pre-selected connector isn't in the catalog. Same blank-step symptom
        // as a failed load, different cause — say so rather than showing
        // nothing.
        setConnectorsError("connector-missing");
        return;
      }
      // Auto-select if only one connector
      if (list.length === 1) {
        selectConnector(list[0]);
      }
    } catch {
      setConnectorsError("unreachable");
    } finally {
      setConnectorsLoading(false);
    }
  }

  const handleDwhReady = useCallback(() => {
    setDwhReady(true);
    setStep("connect");
  }, []);

  const handleAuthValidated = useCallback((valid: boolean) => {
    setAuthValid(valid);
  }, []);

  const handlePixelConfigChange = useCallback(
    (config: VerityPixelConnectorConfig | null, label: string | null) => {
      setPixelConfig(config);
      setPixelWebsiteLabel(label);
    },
    [],
  );

  // True when the caller's account already has a pipeline of this kind for
  // the selected connector AND the connector is single-instance. Multi-
  // instance connectors (registry flag: FACEBOOK, MICROSOFT_ADS,
  // GA4_BQ_EXPORT — this used to be a GA4-only hardcode) enforce uniqueness
  // per instance/dataset in the backend, so the wizard lets the user add
  // another one and greys out already-connected instances instead.
  const existingBlocksSetup =
    !!selectedConnector &&
    !selectedConnector.supports_multiple_instances &&
    (transformOnly
      ? !!selectedConnector.transform_only_exists
      : !!selectedConnector.managed_pipeline_exists);

  // Block advancing past Connect when the chosen connector needs an active
  // PII vault that this account doesn't have yet. Non-vault connectors
  // (requires_pii_vault falsy) are never affected.
  const piiVaultBlocks =
    !!selectedConnector?.requires_pii_vault && !piiVaultReady;

  function canGoNext(): boolean {
    switch (step) {
      case "connect":
        return (
          !!selectedConnector &&
          authValid &&
          !existingBlocksSetup &&
          !piiVaultBlocks
        );
      case "categories":
        return selectedCategories.length > 0;
      default:
        return true;
    }
  }

  function handleNext() {
    const idx = STEP_ORDER.indexOf(step);
    if (idx >= 0 && idx < STEP_ORDER.length - 1) {
      const nextStep = STEP_ORDER[idx + 1];
      // When entering categories step for transform-only: pre-select only available categories
      if (nextStep === "categories" && transformOnly && transformAvailableCategories) {
        setSelectedCategories(transformAvailableCategories);
      }
      // Auto-fill source name when entering confirm step (if empty).
      // Keep it just the connector label — the row's subtitle already shows
      // the disambiguating detail (ad account id, property name, dataset id),
      // and the tier is its own column on the list. Users can still edit.
      //
      // Exception: multi-instance Meta. A second ad account with the bare
      // label would collide on the unique (account, name) constraint, so the
      // default embeds the ad account (name from the picker, else the id) —
      // mirroring the backend's default for unnamed multi-instance sources.
      if (nextStep === "confirm" && !sourceName && selectedConnector) {
        // What the customer typed wins over anything we can compose. It is only
        // set when they created a NEW connection in this run of the wizard;
        // reusing an existing authorization leaves it empty and falls through
        // to the defaults below.
        if (connectionName.trim()) {
          setSourceName(connectionName.trim());
        } else if (
          selectedConnector.connector_type === "FACEBOOK" &&
          !transformOnly &&
          adAccountId.trim()
        ) {
          setSourceName(
            `${selectedConnector.label} (${fbAccountMeta?.account_name || adAccountId.trim()})`,
          );
        } else if (
          // Microsoft Ads is also multi-instance: a second manager with the
          // bare label would collide on the unique (account, name) constraint,
          // so embed the manager (name from the picker, else the id).
          selectedConnector.connector_type === "MICROSOFT_ADS" &&
          !transformOnly &&
          msCustomerId.trim()
        ) {
          setSourceName(
            `${selectedConnector.label} (${msCustomerName || msCustomerId.trim()})`,
          );
        } else if (
          // Search Console is multi-instance too — one source per property, and
          // a tenant may well connect several. The bare label would collide on
          // the unique (account, name) constraint at the second one. The
          // property is embedded VERBATIM: it is the only thing that tells two
          // properties over the same host apart.
          selectedConnector.connector_type === "GOOGLE_SEARCH_CONSOLE" &&
          !transformOnly &&
          gscSiteUrl
        ) {
          setSourceName(`${selectedConnector.label} (${gscSiteUrl})`);
        } else if (
          // One pixel source per website, so a second one under the bare label
          // would collide on the unique (account, name) constraint.
          selectedConnector.connector_type === "VERITY_PIXEL" &&
          pixelWebsiteLabel
        ) {
          setSourceName(`${selectedConnector.label} (${pixelWebsiteLabel})`);
        } else {
          setSourceName(selectedConnector.label);
        }
      }
      setStep(nextStep);
    }
  }

  function handleBack() {
    const idx = STEP_ORDER.indexOf(step);
    if (idx > 0) {
      // Don't go back to dwh-gate if DWH is already ready
      if (STEP_ORDER[idx - 1] === "dwh-gate" && dwhReady) {
        return;
      }
      setStep(STEP_ORDER[idx - 1]);
    }
  }

  async function handleSubmit() {
    if (!selectedConnector) return;
    const isTransformOnly = selectedConnector.connector_type === "GA4_BQ_EXPORT" || !!transformOnly;
    // The Verity Pixel sends no authorization: the backend resolves the
    // account's own warehouse access (setup_routes.py, VERITY_PIXEL branch).
    const isVerityPixel = selectedConnector.connector_type === "VERITY_PIXEL";
    if (!isTransformOnly && !isVerityPixel && !authorizationId) return;
    if (isVerityPixel && !pixelConfig) return;
    // A connector whose registry default is no backfill (the pixel: events
    // only exist from install on) sends an honest 0, which the setup schema
    // admits exactly for such a connector (plan 2026-10-02 step 7.4); the
    // review step hides the backfill row for it.
    const hasBackfill = (selectedConnector.default_backfill_days ?? 395) > 0;
    setSubmitting(true);
    setSubmitError(null);

    // Build connector-specific config
    let connectorConfig: Record<string, unknown> = {};
    if (
      GENERIC_BYO_CONNECTORS.includes(selectedConnector.connector_type) &&
      transformOnly
    ) {
      connectorConfig = {
        source_project_id: transformSourceProjectId,
        source_dataset_id: transformSourceDatasetId,
      };
    } else if (selectedConnector.connector_type === "FACEBOOK") {
      connectorConfig = {
        ad_account_id: adAccountId,
        // Display metadata from the picker: labels this source in the
        // aggregate dashboard's account filter and feeds the mixed-currency
        // warning. The backend validates and stores it best-effort; absent
        // for manually-typed ids.
        ...(fbAccountMeta?.account_name && {
          account_name: fbAccountMeta.account_name,
        }),
        ...(fbAccountMeta?.currency && { currency: fbAccountMeta.currency }),
      };
    } else if (selectedConnector.connector_type === "GOOGLE_ANALYTICS") {
      connectorConfig = { property_id: propertyId, property_name: propertyName };
    } else if (selectedConnector.connector_type === "GOOGLE_ADS") {
      connectorConfig = {
        customer_id: googleAdsCustomerId,
        // Without this, every account reached through an MCC fails with
        // USER_PERMISSION_DENIED — precisely the accounts the manager-grouped
        // picker exists to offer. The backend, the sensor and the API resource
        // all carried the field already; only the wizard never sent it.
        login_customer_id: googleAdsLoginCustomerId,
      };
    } else if (selectedConnector.connector_type === "GA4_BQ_EXPORT") {
      connectorConfig = {
        source_project_id: sourceProjectId,
        source_datasets: sourceDatasets.map((d) => {
          // Optional dashboard label — omitted when blank so the resolver falls
          // back to the source name instead of storing an empty string.
          const label = d.property_name?.trim();
          return {
            dataset_id: d.dataset_id,
            property_id: d.property_id,
            ...(label ? { property_name: label } : {}),
          };
        }),
      };
    } else if (selectedConnector.connector_type === "MICROSOFT_ADS") {
      connectorConfig = {
        // The MANAGER account: names the per-manager raw dataset and is the
        // per-tenant duplicate boundary. The backend re-verifies it against a
        // fresh enumeration — never trusting this value.
        customer_id: msCustomerId,
        account_selection_mode: msAccountSelectionMode,
        // Sent only for a fixed selection; ALL means "every account, including
        // future ones", so an id list would just be stale the moment one is added.
        ...(msAccountSelectionMode === "SPECIFIC" && {
          account_ids: msAccountIds,
        }),
        coverage_preset: msCoveragePreset,
      };
    } else if (selectedConnector.connector_type === "GOOGLE_SEARCH_CONSOLE") {
      connectorConfig = {
        // VERBATIM, deliberately: no trim, no lower-casing, no stripping of the
        // `sc-domain:` prefix. This value is the warehouse union discriminator
        // and Search Console returns rows only under the exact string it gave,
        // so a reshaped one produces a source whose leg matches zero rows. The
        // backend re-validates it against the same pattern and answers 409 when
        // the property is already connected in this account.
        site_url: gscSiteUrl,
      };
    } else if (isVerityPixel && pixelConfig) {
      connectorConfig = { ...pixelConfig };
    }

    // Defence-in-depth: never submit categories that the connector has
    // marked unavailable. Even though selectedCategories is initialized
    // from `c.available` and the CategoriesStep checkbox is disabled for
    // unavailable entries, state can drift across re-renders or back-nav.
    // Filtering here guarantees the API never sees an "unavailable" key.
    const availableKeys = new Set(
      selectedConnector.categories.filter((c) => c.available).map((c) => c.key),
    );
    const submittedCategories = selectedCategories.filter((k) => availableKeys.has(k));

    // Brevo: Verity-ID write-back opt-in from Advanced settings. Gated on the
    // contacts category actually being submitted — the toggle is only shown
    // when contacts is selected, but state can drift if the user enables it,
    // navigates back, and deselects contacts. Omitted when off (default).
    if (
      selectedConnector.connector_type === "BREVO" &&
      verityIdWriteback &&
      submittedCategories.includes("contacts")
    ) {
      connectorConfig = {
        ...connectorConfig,
        verity_id_writeback: { enabled: true },
      };
    }

    // Segmentation opt-in. Gated on every required category actually being
    // submitted — the switch is disabled without them, but state can drift if
    // the user enables it, navigates back and deselects a category. Sent only
    // for connectors that support segmentation at all. Falls back to the
    // transitional singular field when the plural is absent (old backend).
    const segmentationRequiredCategories =
      selectedConnector.segmentation_required_categories ??
      (selectedConnector.segmentation_required_category
        ? [selectedConnector.segmentation_required_category]
        : []);
    const segmentationRequested =
      !!selectedConnector.segmentation_supported &&
      useAsSegmentationSource &&
      segmentationRequiredCategories.every((key) =>
        submittedCategories.includes(key),
      );

    const payload: ManagedSourceSetupPayload = {
      connector_type: selectedConnector.connector_type,
      ...(authorizationId && { authorization_id: authorizationId }),
      categories: submittedCategories,
      schedule_interval: scheduleInterval,
      schedule_hour: scheduleHour,
      ...(scheduleCron && { schedule_cron: scheduleCron }),
      connector_config: connectorConfig,
      ...(!isTransformOnly && { backfill_days: hasBackfill ? backfillDays : 0 }),
      // Signal transform-only mode to backend (GA4_BQ_EXPORT is implicit)
      ...(transformOnly && { ingest_mode: "TRANSFORM_ONLY" }),
      // Only when the customer actually answered the arrival question, and
      // only on a bring-your-own-data create — the backend rejects it on an
      // ingest source, and an untouched question must land on the CONNECTOR
      // default rather than on whatever this client thinks it is.
      ...(isTransformOnly && freshnessMode && { freshness_mode: freshnessMode }),
      name: sourceName || undefined,
      include_in_account_overview: includeInOverview,
      ...(selectedConnector.segmentation_supported && {
        connector_segmentation_requested: segmentationRequested,
      }),
      ...(activeWorkspaceId && { workspace_ids: [activeWorkspaceId] }),
    };

    try {
      const created = await setupManagedSource(payload);
      setSuccess(true);
      setCreatedSource(created);
      onCreated?.(created);
      notifications.show({
        title: "Pipeline created",
        message: `Your ${selectedConnector.label} data pipeline has been set up successfully.`,
        color: "green",
      });
      // Transform-only sources have no ingest run to watch, so without this
      // the customer is left wondering whether anything happens at all. The
      // by-connector form is used because the answer is about the connector's
      // account-level models, not this one source. Fire-and-forget: creation
      // has already succeeded, so a failure here must stay invisible.
      if (isTransformOnly) {
        getTransformOutlookByConnector(selectedConnector.connector_type)
          .then((outlook) => {
            const { toast, color } = outlookMessage(outlook);
            if (!toast) return;
            notifications.show({
              title: OUTLOOK_TOAST_TITLE,
              message: toast,
              color,
            });
          })
          .catch(() => {
            /* Silent — nothing the customer could act on */
          });
      }
    } catch (e: any) {
      setSubmitError(e?.message || "Failed to set up pipeline");
    } finally {
      setSubmitting(false);
    }
  }

  const contentRef = useRef<HTMLDivElement>(null);

  // Scroll to top when step changes
  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 });
  }, [step]);

  const visibleStepIndex = VISIBLE_STEPS.findIndex((s) => s.key === step);

  const connectorLabel =
    selectedConnector?.label ||
    (initialConnectorType && CONNECTOR_TYPE_LABELS[initialConnectorType]) ||
    "your data source";

  // DWH gate: show explanation, then open DwhSetupModal on confirm
  if (step === "dwh-gate") {
    return (
      <>
        <AppModal
          opened={opened && !dwhModalOpen}
          onClose={onClose}
          size="sm"
          title="Data warehouse required"
          // This wizard owns its raised layer (300, over the assistant drawer).
          zIndex={zIndex}
        >
          <Stack align="center" gap="lg">
            <ThemeIcon size={64} radius="xl" variant="light" color="blue">
              <IconAlertTriangle size={32} />
            </ThemeIcon>
            <Text size="sm" c="dimmed" ta="center" maw={400}>
              Before connecting {connectorLabel}, you need a data warehouse to
              store your pipeline data. This is a one-time setup that takes
              just a few minutes.
            </Text>
            <Button size="md" onClick={() => setDwhModalOpen(true)}>
              Set up data warehouse
            </Button>
          </Stack>
        </AppModal>

        <DwhSetupModal
          opened={dwhModalOpen}
          zIndex={zIndex}
          onClose={() => {
            setDwhModalOpen(false);
            onClose();
          }}
          onReady={() => {
            setDwhModalOpen(false);
            handleDwhReady();
          }}
          contextLabel={connectorLabel}
        />
      </>
    );
  }

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      zIndex={zIndex}
      title="Set up data pipeline"
      steps={VISIBLE_STEPS}
      activeStep={visibleStepIndex}
      allStepsComplete={success}
      bodyRef={contentRef}
      bodyMinHeight={400}
      bodyMaxHeight="calc(80vh - 160px)"
      footer={
        // The success screen has its own close affordance; a Cancel/Continue
        // row underneath it would offer to abandon work that is already done.
        success ? undefined : (
          <Group justify="space-between">
            <Button
              variant="subtle"
              color="gray"
              leftSection={<IconArrowLeft size={16} />}
              onClick={step === "connect" ? onClose : handleBack}
            >
              {step === "connect" ? "Cancel" : "Back"}
            </Button>

            {step !== "confirm" && (
              <Button
                rightSection={<IconArrowRight size={16} />}
                onClick={handleNext}
                disabled={!canGoNext()}
              >
                Continue
              </Button>
            )}
          </Group>
        )
      }
    >

        {step === "connect" && (
          <Stack gap="lg">
            {/* Loading + failure states for the connector catalog. Without
                these the step renders as an empty white box, because every
                auth step below requires a selected connector. */}
            {connectorsLoading && !connectorsError && (
              <Group gap="sm">
                <Loader size="sm" />
                <Text size="sm" c="dimmed">Loading available sources…</Text>
              </Group>
            )}

            {connectorsError && (
              <Alert
                color="red"
                icon={<IconAlertTriangle size={16} />}
                title={
                  connectorsError === "unreachable"
                    ? "We couldn't load the available sources"
                    : "That source isn't available"
                }
              >
                <Stack gap="sm" align="flex-start">
                  <Text size="sm">
                    {connectorsError === "unreachable"
                      ? "Something went wrong while loading the list of sources you can connect. Check your connection and try again — if it keeps happening, contact support."
                      : "This source isn't available on your account right now. Close this window and pick a different one, or contact support if you expected it to be here."}
                  </Text>
                  {connectorsError === "unreachable" && (
                    <Button
                      size="xs"
                      variant="light"
                      loading={connectorsLoading}
                      onClick={() => void loadConnectors()}
                    >
                      Try again
                    </Button>
                  )}
                </Stack>
              </Alert>
            )}

            {/* Connector selection (when multiple connectors available) */}
            {connectors.length > 1 && !initialConnectorType && (
              <Group gap="sm" wrap="wrap">
                {connectors.map((c) => (
                  <UnstyledButton
                    key={c.connector_type}
                    onClick={() => selectConnector(c)}
                    style={{
                      flex: 1,
                      minWidth: 200,
                    }}
                  >
                    <Box
                      p="md"
                      style={{
                        border: `2px solid ${
                          selectedConnector?.connector_type === c.connector_type
                            ? "var(--mantine-color-blue-5)"
                            : "var(--app-border, var(--mantine-color-gray-2))"
                        }`,
                        borderRadius: "var(--mantine-radius-md)",
                        backgroundColor:
                          selectedConnector?.connector_type === c.connector_type
                            ? "var(--mantine-color-blue-0)"
                            : undefined,
                        cursor: "pointer",
                        transition: "border-color 150ms, background-color 150ms",
                      }}
                    >
                      <Group gap="sm" wrap="nowrap">
                        <ConnectorIcon type={c.connector_type} size={24} />
                        <Stack gap={0}>
                          <Text fw={600} size="sm">{c.label}</Text>
                          <Text size="xs" c="dimmed" lineClamp={1}>{c.description}</Text>
                        </Stack>
                      </Group>
                    </Box>
                  </UnstyledButton>
                ))}
              </Group>
            )}

            {/* Connector already set up — warn up-front instead of failing at submit. */}
            {existingBlocksSetup && selectedConnector && (
              <Alert
                color="yellow"
                icon={<IconAlertTriangle size={16} />}
                title={
                  transformOnly
                    ? `${selectedConnector.label} is already set up`
                    : `A ${selectedConnector.label} pipeline already exists`
                }
              >
                Your account is limited to one
                {" "}
                {transformOnly ? "transform-only" : "managed"} pipeline per connector.
                Manage the existing one from your data sources list, or pick a different
                connector to continue.
              </Alert>
            )}

            {/* PII vault gate — a vault-requiring connector (e.g. Brevo) needs
                an active PII vault first. Connector-specific, so it lives
                inline once the connector is chosen (vs the pre-step DWH gate).
                Mirrors the "Set up your DWH" link pattern. */}
            {piiVaultBlocks && selectedConnector && (
              <Alert
                color="grape"
                icon={<IconShieldLock size={16} />}
                title="PII vault required"
              >
                <Stack gap="xs">
                  <Text size="sm">
                    {selectedConnector.label} handles customer email addresses,
                    which {branding.productName} keeps encrypted in your PII vault. Set up the
                    PII vault before connecting this source.
                  </Text>
                  <Anchor
                    component={Link}
                    to="/settings/account?tab=data-warehouse"
                    size="sm"
                    fw={600}
                    onClick={onClose}
                  >
                    Set up your PII vault →
                  </Anchor>
                </Stack>
              </Alert>
            )}

            {/* Dynamic auth step based on selected connector. Hidden when an
                existing pipeline already blocks setup — no point in letting
                the user fill in credentials they can't submit. */}
            {!existingBlocksSetup &&
              !!selectedConnector &&
              GENERIC_BYO_CONNECTORS.includes(selectedConnector.connector_type) &&
              transformOnly && (
                <GenericTransformAuthStep
                  sourceProjectId={transformSourceProjectId}
                  sourceDatasetId={transformSourceDatasetId}
                  connectorType={selectedConnector.connector_type}
                  onConfigChange={(p, d) => {
                    setTransformSourceProjectId(p);
                    setTransformSourceDatasetId(d);
                  }}
                  onValidated={handleAuthValidated}
                  onAvailableCategories={setTransformAvailableCategories}
                />
              )}

            {!existingBlocksSetup && selectedConnector?.connector_type === "FACEBOOK" && !transformOnly && (
              <FacebookAuthStep
                authorizationId={authorizationId}
                adAccountId={adAccountId}
                onAuthorizationChange={setAuthorizationId}
                onAdAccountChange={setAdAccountId}
                onConnectionNameChange={setConnectionName}
                onValidated={handleAuthValidated}
                connectedInstances={selectedConnector.existing_instance_identifiers}
                onAdAccountMetaChange={setFbAccountMeta}
              />
            )}

            {!existingBlocksSetup && selectedConnector?.connector_type === "GOOGLE_ANALYTICS" && (
              <GoogleAnalyticsAuthStep
                authorizationId={authorizationId}
                propertyId={propertyId}
                propertyName={propertyName}
                onAuthorizationChange={setAuthorizationId}
                onPropertyChange={(id, name) => {
                  setPropertyId(id);
                  setPropertyName(name);
                }}
                onConnectionNameChange={setConnectionName}
                onValidated={handleAuthValidated}
              />
            )}

            {!existingBlocksSetup && selectedConnector?.connector_type === "GOOGLE_ADS" && !transformOnly && (
              <GoogleAdsAuthStep
                authorizationId={authorizationId}
                customerId={googleAdsCustomerId}
                onAuthorizationChange={setAuthorizationId}
                onCustomerIdChange={(id, loginId) => {
                  setGoogleAdsCustomerId(id);
                  setGoogleAdsLoginCustomerId(loginId);
                }}
                onConnectionNameChange={setConnectionName}
                onValidated={handleAuthValidated}
              />
            )}

            {!existingBlocksSetup && selectedConnector?.connector_type === "GOOGLE_SEARCH_CONSOLE" && !transformOnly && (
              <GoogleSearchConsoleAuthStep
                authorizationId={authorizationId}
                siteUrl={gscSiteUrl}
                onAuthorizationChange={setAuthorizationId}
                onSiteUrlChange={setGscSiteUrl}
                onConnectionNameChange={setConnectionName}
                onValidated={handleAuthValidated}
              />
            )}

            {!existingBlocksSetup && selectedConnector?.connector_type === "MICROSOFT_ADS" && !transformOnly && (
              <MicrosoftAdsAuthStep
                authorizationId={authorizationId}
                availableIdentityProviders={
                  selectedConnector.available_identity_providers
                }
                customerId={msCustomerId}
                customerName={msCustomerName}
                accountSelectionMode={msAccountSelectionMode}
                accountIds={msAccountIds}
                coveragePreset={msCoveragePreset}
                onAuthorizationChange={setAuthorizationId}
                onCustomerIdChange={(id, name) => {
                  setMsCustomerId(id);
                  setMsCustomerName(name);
                }}
                onAccountSelectionModeChange={setMsAccountSelectionMode}
                onAccountIdsChange={setMsAccountIds}
                onCoveragePresetChange={setMsCoveragePreset}
                onConnectionNameChange={setConnectionName}
                onValidated={handleAuthValidated}
              />
            )}

            {!existingBlocksSetup && selectedConnector?.connector_type === "GA4_BQ_EXPORT" && (
              <GA4BqExportAuthStep
                sourceProjectId={sourceProjectId}
                sourceDatasets={sourceDatasets}
                authorizationId={authorizationId}
                onAuthorizationChange={setAuthorizationId}
                onConfigChange={(p, ds) => {
                  setSourceProjectId(p);
                  setSourceDatasets(ds);
                }}
                onValidated={handleAuthValidated}
              />
            )}

            {!existingBlocksSetup && !piiVaultBlocks && selectedConnector?.connector_type === "BREVO" && (
              <BrevoAuthStep
                authorizationId={authorizationId}
                onAuthorizationChange={setAuthorizationId}
                onValidated={handleAuthValidated}
              />
            )}

            {!existingBlocksSetup && !piiVaultBlocks && selectedConnector?.connector_type === "ACTIVECAMPAIGN" && (
              <ActiveCampaignAuthStep
                authorizationId={authorizationId}
                onAuthorizationChange={setAuthorizationId}
                onValidated={handleAuthValidated}
              />
            )}

            {!existingBlocksSetup && !piiVaultBlocks && selectedConnector?.connector_type === "KLAVIYO" && (
              <KlaviyoAuthStep
                authorizationId={authorizationId}
                onAuthorizationChange={setAuthorizationId}
                onValidated={handleAuthValidated}
              />
            )}

            {/* Every connector with a generic credential form (catalogue flag,
                no per-vendor branch). */}
            {!existingBlocksSetup && !piiVaultBlocks && selectedConnector?.has_credential_form && (
              <CredentialAuthStep
                key={selectedConnector.connector_type}
                connectorType={selectedConnector.connector_type}
                label={selectedConnector.label}
                authType={selectedConnector.auth_type}
                authorizationId={authorizationId}
                onAuthorizationChange={setAuthorizationId}
                onConnectionLabelChange={setCredentialConnectionLabel}
                onValidated={handleAuthValidated}
              />
            )}

            {!existingBlocksSetup && !piiVaultBlocks && selectedConnector?.connector_type === "SHOPIFY" && (
              <ShopifyAuthStep
                authorizationId={authorizationId}
                onAuthorizationChange={setAuthorizationId}
                onStoreLabelChange={setShopifyStoreLabel}
                onConnectionNameChange={setConnectionName}
                onValidated={handleAuthValidated}
              />
            )}

            {!existingBlocksSetup && selectedConnector?.connector_type === "VERITY_PIXEL" && !transformOnly && (
              <VerityPixelWebsiteStep
                onConfigChange={handlePixelConfigChange}
                onValidated={handleAuthValidated}
              />
            )}

            {!selectedConnector && connectors.length > 1 && !initialConnectorType && (
              <Text size="sm" c="dimmed" ta="center" py="xl">
                Select a connector above to continue.
              </Text>
            )}
          </Stack>
        )}

        {step === "categories" && selectedConnector && (
          <CategoriesStep
            categories={selectedConnector.categories}
            selected={selectedCategories}
            onChange={setSelectedCategories}
            unavailableCategories={transformOnly && transformAvailableCategories
              ? selectedConnector.categories
                  .filter((c) => !transformAvailableCategories.includes(c.key))
                  .map((c) => c.key)
              : undefined
            }
          />
        )}

        {/* Verity Pixel: once the source exists, what the customer needs next is
            the snippet, not a pipeline launch to watch (there is no ingest run;
            events stream in from the website). */}
        {step === "confirm" &&
          selectedConnector?.connector_type === "VERITY_PIXEL" &&
          success &&
          createdSource &&
          pixelSiteKeyOf(createdSource.config) && (
            <Stack gap="md">
              <Stack gap={2}>
                <Text fw={700} size="lg">Your pixel is ready to install</Text>
                <Text size="sm" c="dimmed">
                  Add the snippet to your website. This page updates when the first
                  event arrives.
                </Text>
              </Stack>
              <PixelInstallAfterLaunch
                siteKey={pixelSiteKeyOf(createdSource.config) as string}
              />
              <Group justify="flex-end">
                <Button onClick={onClose}>Done</Button>
              </Group>
            </Stack>
          )}

        {step === "confirm" &&
          selectedConnector &&
          !(
            selectedConnector.connector_type === "VERITY_PIXEL" &&
            success &&
            createdSource &&
            pixelSiteKeyOf(createdSource.config)
          ) && (
          <ReviewStep
            sourceName={sourceName}
            onSourceNameChange={setSourceName}
            connectorLabel={selectedConnector.label}
            connectorType={selectedConnector.connector_type}
            authorizationName={null}
            accountLabel={
              GENERIC_BYO_CONNECTORS.includes(selectedConnector.connector_type) && transformOnly
                ? `${transformSourceProjectId}.${transformSourceDatasetId}`
                : selectedConnector.connector_type === "GOOGLE_ANALYTICS"
                  ? propertyName || propertyId
                  : selectedConnector.connector_type === "GOOGLE_ADS"
                    ? googleAdsCustomerId
                    : selectedConnector.connector_type === "GOOGLE_SEARCH_CONSOLE"
                      // Verbatim, prefix and all — shortening it here would hide
                      // the one thing that tells a domain property apart from a
                      // URL-prefix property over the same host.
                      ? gscSiteUrl
                    : selectedConnector.connector_type === "VERITY_PIXEL"
                      ? pixelWebsiteLabel || ""
                    : selectedConnector.connector_type === "MICROSOFT_ADS"
                      ? msCustomerName || msCustomerId
                      : selectedConnector.connector_type === "GA4_BQ_EXPORT"
                        ? `${sourceDatasets.length} ${sourceDatasets.length === 1 ? "dataset" : "datasets"}`
                        : selectedConnector.connector_type === "BREVO"
                          ? "All campaign + transactional events"
                          : selectedConnector.connector_type === "ACTIVECAMPAIGN"
                            ? "All campaign + automation email engagement"
                          : selectedConnector.connector_type === "KLAVIYO"
                            ? "All campaign, flow + transactional events"
                            : selectedConnector.connector_type === "SHOPIFY"
                              ? shopifyStoreLabel || "Connected store"
                            : selectedConnector.has_credential_form
                              ? credentialConnectionLabel || "Connected account"
                              : adAccountId
            }
            accountLabelTitle={
              GENERIC_BYO_CONNECTORS.includes(selectedConnector.connector_type) && transformOnly
                ? "Source Dataset"
                : selectedConnector.connector_type === "GOOGLE_ANALYTICS"
                  ? "Property"
                  : selectedConnector.connector_type === "GOOGLE_ADS"
                    ? "Customer ID"
                    : selectedConnector.connector_type === "GOOGLE_SEARCH_CONSOLE"
                      ? "Property"
                    : selectedConnector.connector_type === "VERITY_PIXEL"
                      ? "Website"
                    : selectedConnector.connector_type === "MICROSOFT_ADS"
                      ? "Manager account"
                      : selectedConnector.connector_type === "GA4_BQ_EXPORT"
                        ? "Datasets"
                        : selectedConnector.connector_type === "BREVO"
                          ? "Scope"
                          : selectedConnector.connector_type === "ACTIVECAMPAIGN"
                            ? "Scope"
                          : selectedConnector.connector_type === "KLAVIYO"
                            ? "Scope"
                            : selectedConnector.connector_type === "SHOPIFY"
                              ? "Store"
                            : selectedConnector.has_credential_form
                              ? "Connection"
                              : "Ad Account"
            }
            isTransformOnly={selectedConnector.connector_type === "GA4_BQ_EXPORT" || !!transformOnly}
            hasBackfill={(selectedConnector.default_backfill_days ?? 395) > 0}
            categories={selectedConnector.categories}
            selectedCategories={selectedCategories}
            scheduleInterval={scheduleInterval}
            onScheduleChange={setScheduleInterval}
            scheduleHour={scheduleHour}
            onScheduleHourChange={setScheduleHour}
            scheduleCron={scheduleCron}
            onScheduleCronChange={setScheduleCron}
            backfillDays={backfillDays}
            onBackfillDaysChange={setBackfillDays}
            freshnessMode={
              freshnessMode ??
              selectedConnector.default_freshness_mode ??
              "SCHEDULED"
            }
            onFreshnessModeChange={setFreshnessMode}
            includeInOverview={includeInOverview}
            onIncludeInOverviewChange={setIncludeInOverview}
            verityIdWriteback={verityIdWriteback}
            onVerityIdWritebackChange={setVerityIdWriteback}
            segmentationSupported={selectedConnector.segmentation_supported}
            segmentationRequiredCategories={
              selectedConnector.segmentation_required_categories ??
              (selectedConnector.segmentation_required_category
                ? [selectedConnector.segmentation_required_category]
                : null)
            }
            useAsSegmentationSource={useAsSegmentationSource}
            onUseAsSegmentationSourceChange={setUseAsSegmentationSource}
            onSubmit={handleSubmit}
            submitting={submitting}
            error={submitError}
            success={success}
            createdSource={createdSource}
            onClose={onClose}
          />
        )}
    </AppModal>
  );
}
